/**
 * Region settlement quest boards — generated missions as a pure function of
 * `(settlement.id, epoch)`. Every client and the server derive the identical
 * offer list with zero data transfer; only the self-describing quest id
 * (`${settlement.id}:q:${epoch}:${i}`) ever needs to travel, and the server
 * re-derives the full def from it (never trusting a client-sent def).
 */
import { pick, rangeInt, rngFromString } from "../rng.js";
import { ITEMS } from "../rpg.js";
import { PlanetSampler, type PlanetDef, type SystemDef } from "../universe.js";
import { collapseRegion } from "../worldgen/collapse.js";
import { materializeRegion } from "../worldgen/growth.js";
import { indexRegionRoster, regionQuestRoster } from "../worldgen/roster.js";
import { parseRegionSettlementId, regionSettlements, type RegionSettlement } from "../worldgen/settlements.js";
import type { NpcDef } from "../npcs.js";
import type { ObjectiveDef, QuestDef, RewardDef } from "./types.js";

/** Board rotation period (s) — boards reroll every 6 hours. */
export const QUEST_EPOCH_SEC = 6 * 3600;

/** The current board epoch for a wall-clock time (seconds). */
export function questEpoch(tSec: number): number {
  return Math.floor(tSec / QUEST_EPOCH_SEC);
}

/** How many quests a settlement's board offers. Outposts/hamlets: none. */
const BOARD_SIZE = [0, 0, 3, 4, 6, 6] as const;

/** Reward multiplier by tier (bigger places pay better). */
const TIER_MUL = [1, 1, 1, 1.5, 2, 2.5] as const;

/** The four board templates, rotated per board so every board has variety. */
const TEMPLATES = ["bounty", "collect", "deliver", "talk"] as const;
type Template = (typeof TEMPLATES)[number];

/** Ores the planet plausibly bears (mirrors resources.ts archetype leans). */
function plausibleOres(planet: PlanetDef): string[] {
  switch (planet.archetype) {
    case "volcanic":
      return ["ore-volatite", "ore-ferrite"];
    case "desert":
      return ["ore-volatite", "ore-ferrite"];
    case "ice":
      return ["ore-cryonite", "ore-ferrite"];
    case "barren":
      return ["ore-ferrite", "ore-volatite", "ore-cryonite"];
    default:
      return ["ore-ferrite", "ore-volatite", "ore-cryonite"];
  }
}

/** Deliverable goods a courier plausibly carries. */
const DELIVER_GOODS = ["scrap-alloy", "ore-ferrite", "medkit"] as const;
/** Safe catalog ids for the occasional item reward. */
const REWARD_ITEMS = ["medkit", "scrap-alloy", "ammo-heavy", "ammo-light", "fuel-cell"] as const;
/** Roles a deliver quest may name as the recipient. */
const DELIVER_ROLES = ["keeper", "trader", "warden", "elder"] as const;

function itemName(id: string): string {
  return ITEMS[id]?.name ?? id;
}

/**
 * The board for a settlement at a given epoch. Village 3 / town 4 / city 6;
 * outposts and hamlets have no board (empty list). Templates rotate so a
 * board is never four of the same, and rewards scale with tier + count.
 */
export function settlementQuestBoard(
  settlement: RegionSettlement, planet: PlanetDef, roster: readonly NpcDef[], epoch: number,
): QuestDef[] {
  const size = BOARD_SIZE[settlement.tier] ?? 0;
  if (size <= 0) return [];
  const rng = rngFromString(`${settlement.id}:quests:${epoch}`);
  const ores = plausibleOres(planet);
  const mul = TIER_MUL[settlement.tier] ?? 1;
  const rotOff = Math.floor(rng() * TEMPLATES.length);
  const out: QuestDef[] = [];

  for (let i = 0; i < size; i++) {
    const id = `${settlement.id}:q:${epoch}:${i}`;
    const tmpl: Template = TEMPLATES[(i + rotOff) % TEMPLATES.length];
    let objective: ObjectiveDef;
    let title: string;
    let blurb: string;
    let credits: number;

    if (tmpl === "bounty") {
      const n = rangeInt(rng, 3, 6);
      objective = { kind: "kill", n, text: `Destroy ${n} hostiles near ${settlement.name}` };
      title = "Standing Bounty";
      blurb = `${settlement.name} pays for cleared skies: ${n} hostiles menace the approaches.`;
      credits = Math.round((50 + 35 * n) * mul);
    } else if (tmpl === "collect") {
      const item = pick(rng, ores);
      const n = rangeInt(rng, 3, 8);
      objective = { kind: "collect", n, item, text: `Mine ${n} ${itemName(item)}` };
      title = "Supply Order";
      blurb = `The ${settlement.name} works need ${n} ${itemName(item)}. Mine it, get paid.`;
      credits = Math.round((40 + 25 * n) * mul);
    } else if (tmpl === "deliver") {
      const item = pick(rng, DELIVER_GOODS);
      const n = rangeInt(rng, 2, 5);
      const role = pick(rng, DELIVER_ROLES);
      objective = {
        kind: "deliver",
        n,
        item,
        npc: { role, town: "here" },
        text: `Deliver ${n} ${itemName(item)} to the ${role}`,
      };
      title = "Courier Run";
      blurb = `Carry ${n} ${itemName(item)} to the ${role} of ${settlement.name}.`;
      credits = Math.round((60 + 40 * n) * mul);
    } else {
      // talk — a concrete roster persona (fallback to keeper / roster[0]).
      const person = roster.find((n) => n.role === "trader") ?? roster.find((n) => n.role === "keeper") ?? roster[i % Math.max(1, roster.length)] ?? roster[0];
      const npcId = person?.id;
      const npcName = person?.name ?? "the keeper";
      objective = {
        kind: "talk",
        n: 1,
        npc: npcId ? { npc: npcId } : { role: "keeper", town: "here" },
        text: `Speak with ${npcName}`,
      };
      title = "Word Around Town";
      blurb = `Look up ${npcName} in ${settlement.name} — they've been asking after new faces.`;
      credits = Math.round(80 * mul);
    }

    const reward: RewardDef = { credits };
    if (rng() < 0.3) {
      const rid = pick(rng, REWARD_ITEMS);
      const rn = rid === "ammo-heavy" || rid === "ammo-light" ? rangeInt(rng, 15, 40) : 1;
      reward.items = [{ id: rid, n: rn }];
    }

    out.push({
      id,
      category: "town",
      title,
      blurb,
      stages: [{ objectives: [objective], journal: objective.text }],
      reward,
    });
  }

  return out;
}

/**
 * Parse a board quest id back to its parts, or null for a non-board id
 * (campaign `camp:*`, garbage). Town seeds themselves contain colons, so the
 * id is parsed from the RIGHT: `${settlementId}:q:${epoch}:${index}`.
 */
export function parseQuestId(q: string): { settlementId: string; townSeed: string; ep: number; i: number } | null {
  if (typeof q !== "string") return null;
  const parts = q.split(":");
  if (parts.length < 4) return null;
  const i = Number(parts[parts.length - 1]);
  const ep = Number(parts[parts.length - 2]);
  if (parts[parts.length - 3] !== "q") return null;
  if (!Number.isInteger(i) || i < 0 || !Number.isInteger(ep) || ep < 0) return null;
  const settlementId = parts.slice(0, parts.length - 3).join(":");
  if (settlementId.length === 0) return null;
  return { settlementId, townSeed: settlementId, ep, i };
}

/**
 * Re-derive one board QuestDef from its id inside a system: find the planet
 * whose seed prefixes the settlement id, match the site, regenerate that site's
 * board at epoch `ep`, and return the def with the matching id (else null).
 * Mirrors the prefix-match style of server/src/npcs.ts resolveTalk.
 */
export function resolveBoardQuest(system: SystemDef, q: string, ep: number): QuestDef | null {
  const parsed = parseQuestId(q);
  if (!parsed) return null;
  const identity = parseRegionSettlementId(parsed.settlementId);
  if (!identity) return null;
  const planet = system.planets.find((p) => p.seed === identity.planetSeed);
  if (!planet) return null;
  const sampler = new PlanetSampler(planet);
  const graph = materializeRegion(sampler, identity.regionId);
  const chronicle = collapseRegion(sampler, graph);
  const settlement = regionSettlements(sampler, graph, chronicle).find((site) => site.id === parsed.settlementId);
  const indexed = indexRegionRoster(graph, chronicle).settlements.find((entry) => entry.settlementId === parsed.settlementId);
  if (!settlement || !indexed || settlement.board === null) return null;
  const board = settlementQuestBoard(settlement, planet, regionQuestRoster(graph, indexed), ep);
  return board.find((d) => d.id === q) ?? null;
}
